/**
 * SESSION DIAGNOSTICS — evidence for "it keeps signing us out" (2026-09-29).
 *
 * Gymnastics of York's shared staff login is signed out seconds after
 * signing in, several times a day. The server's auth_rejections show the
 * pattern exactly: a successful sign-in, then 2–3 seconds later a
 * session-critical request (/auth/capabilities) carrying a token issued
 * WEEKS earlier (2026-09-05 or 2026-09-16), refused as expired. The current
 * code reads the token from localStorage on every request and sign-in
 * writes the new one, so where the old one comes from could not be settled
 * by reading the code. This records the facts at the moment it happens:
 *
 *   - how long this page (tab) has been open, and which page it is;
 *   - the refused token's issue time next to the STORED token's, and whether
 *     they are the same token (not the same → the request did not read
 *     storage; same → something wrote the old token back);
 *   - how full localStorage is (a failed write leaves the old token in place).
 *
 * A server-side rejection with NO matching report means the request came
 * from an app version that predates this file, i.e. a tab left open since
 * before it shipped.
 *
 * Never throws; observability must not change what the user sees.
 */
import { captureError } from "../utils/sentry";
import { AUTH_TOKEN_KEY, tokenIssuedAt } from "./authTokenStore";
import { localStorageAvailable, safeGetItem, safeLocalKeys } from "@mediatechgroup/app-version";

const PAGE_LOADED_AT = Date.now();

/** localStorage size in characters and key count, or null when unreadable. */
export function storageFootprint(): { chars: number; keys: number } | null {
  // Unreadable storage (blocked) is null, not "empty".
  if (!localStorageAvailable()) return null;
  try {
    let chars = 0;
    const keys = safeLocalKeys();
    for (const key of keys) {
      chars += key.length + (safeGetItem(key) || "").length;
    }
    return { chars, keys: keys.length };
  } catch {
    return null;
  }
}

/**
 * The largest localStorage keys, "key:chars" joined by commas. Names the
 * culprit when storage is full; a near-empty result while writes still fail
 * points at the computer's disk being full instead.
 */
export function largestStorageKeys(limit = 5): string | undefined {
  try {
    const sizes: Array<[string, number]> = [];
    for (const key of safeLocalKeys()) {
      sizes.push([key, key.length + (safeGetItem(key) || "").length]);
    }
    sizes.sort((a, b) => b[1] - a[1]);
    return sizes.slice(0, limit).map(([k, n]) => `${k.slice(0, 40)}:${n}`).join(",") || undefined;
  } catch {
    return undefined;
  }
}

function readStored(): string | null {
  try {
    return safeGetItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function currentPath(): string {
  try {
    return window.location.pathname;
  } catch {
    return "";
  }
}

/**
 * A session-critical endpoint refused this tab's token. Report what the tab
 * knew at that moment.
 */
export function reportSessionRejection(rejectedToken: string | null, endpoint: string, code?: string): void {
  try {
    const stored = readStored();
    captureError(
      new Error("Session rejected by the server"),
      {
        component: "session",
        operation: "sessionRejected",
        endpoint,
        code: code || undefined,
        page: currentPath(),
        tabOpenMinutes: Math.round((Date.now() - PAGE_LOADED_AT) / 60000),
        rejectedIssuedAt: tokenIssuedAt(rejectedToken) ?? undefined,
        storedIssuedAt: tokenIssuedAt(stored) ?? undefined,
        rejectedIsStored: !!rejectedToken && rejectedToken === stored,
        storedPresent: !!stored,
        storageChars: storageFootprint()?.chars,
        storageKeys: storageFootprint()?.keys,
        largestKeys: largestStorageKeys(),
        visibility: typeof document !== "undefined" ? document.visibilityState : undefined,
      },
      "warning"
    );
  } catch {
    /* never let diagnostics change behavior */
  }
}

/**
 * Sign-in stored a token that did not stick (storage full, or blocked).
 * The old token, if any, is still in place.
 */
export function reportSignInTokenNotSaved(issued: string, error?: unknown): void {
  try {
    const stored = readStored();
    captureError(
      error || new Error("Sign-in token was not saved"),
      {
        component: "session",
        operation: "signInTokenNotSaved",
        page: currentPath(),
        issuedAt: tokenIssuedAt(issued) ?? undefined,
        storedIssuedAt: tokenIssuedAt(stored) ?? undefined,
        storageChars: storageFootprint()?.chars,
        storageKeys: storageFootprint()?.keys,
        largestKeys: largestStorageKeys(),
      },
      "error"
    );
  } catch {
    /* never let diagnostics change behavior */
  }
}

/**
 * Sign-in threw (a network failure, or a success on the server followed by a
 * failure in the browser — e.g. a full storage). This used to show "An error
 * occurred during login" and report nothing.
 */
export function reportSignInFailed(error: unknown): void {
  try {
    captureError(
      error,
      {
        component: "session",
        operation: "signInFailed",
        page: currentPath(),
        storedIssuedAt: tokenIssuedAt(readStored()) ?? undefined,
        storageChars: storageFootprint()?.chars,
        storageKeys: storageFootprint()?.keys,
        largestKeys: largestStorageKeys(),
      },
      "error"
    );
  } catch {
    /* never let diagnostics change behavior */
  }
}
